import moment from "moment";

import {
    STANDARD_CHART_STYLE,
    BOLD_CHART_STYLE,
    CREDITS,
    accountWithType,
    formatTooltip,
    formatChartMoney,
    asCoordinateArray,
    createSeries,
    asTuple,
    grossOrNet,
    createZIndexedSeries
} from "commons/src/ChartFunctions";
import {
    convertIdToReal,
    getDefaultAnalysisDateRange,
    LAST_TAX,
    ALL_DATES,
    FORMAT
} from "commons/src/NuDateUtils";

export const getConfig = (isHome, data, account, rangeId, selectedAccounts, theme) => {
    const normalStyle = Object.assign({}, STANDARD_CHART_STYLE);
    const boldStyle = Object.assign(BOLD_CHART_STYLE);

    const dateRange = rangeId ? convertIdToReal(rangeId) : getDefaultAnalysisDateRange();
    const dateRangeStart = dateRange.startDate;
    const dateRangeEnd = dateRange.endDate;

    let minYValue;
    let maxYValue;
    let series;
    let graphStartDate;
    if (
        !data ||
        !data?.data ||
        data.ERROR ||
        Date.parse(data.actualEndDate) < Date.parse(dateRangeStart)
    ) {
        const maxY = 11000;
        series = [
            {
                data: [{ x: 0, y: 0 }],
                name: isHome
                    ? "Portfolio total"
                    : accountWithType(account, selectedAccounts) + " valuation"
            }
        ];
        minYValue = 0;
        maxYValue = maxY;
    } else {
        series = generateSeries(isHome, data, account, selectedAccounts, theme);
    }

    const firstDateInSeries = moment(series[0].data[0].x);

    if (
        dateRangeStart.startOf("day") >= firstDateInSeries.startOf("day") ||
        rangeId === LAST_TAX.id
    ) {
        graphStartDate = dateRangeStart;
    } else if (rangeId === ALL_DATES.id) {
        graphStartDate = moment(firstDateInSeries);
    } else {
        graphStartDate = moment(firstDateInSeries).subtract(1, "month");
    }

    if (isHome) {
        normalStyle.color = theme.tertiary;
        boldStyle.color = theme.tertiary;
    }

    const isAccountStartVisible = graphStartDate.startOf("day") < firstDateInSeries.startOf("day");

    const millisPerHour = 36e6;
    const hoursPerDay = 24;

    const config = {
        nuvars: {
            selectedAccounts: selectedAccounts
        },
        chart: {
            zoomType: "x",
            backgroundColor: theme.tertiary,
            style: {
                color: theme.secondary
            }
        },
        credits: CREDITS,
        plotOptions: {
            series: {
                turboThreshold: 50000
            },
            flags: {
                style: {
                    fontSize: "8px",
                    fontWeight: "normal"
                }
            },
            area: {
                fillOpacity: 0.25
            }
        },
        rangeSelector: {
            enabled: false
        },
        title: {
            align: "left",
            text:
                isHome &&
                `${graphStartDate.format(FORMAT.SLASH_SEPARATED_ABBR_YR)} - ${dateRangeEnd.format(
                    FORMAT.SLASH_SEPARATED_ABBR_YR
                )}`,
            style: boldStyle
        },
        legend: {
            enabled: true,
            align: "center",
            layout: "horizontal",
            verticalAlign: "bottom",
            maxHeight: 200,
            itemStyle: normalStyle,
            labelFormatter: function () {
                return this.chart.options.nuvars.selectedAccounts[this.name]
                    ? this.name +
                          " " +
                          this.chart.options.nuvars.selectedAccounts[this.name].accountType
                    : this.name;
            },
            symbolHeight: 10,
            symbolWidth: 20
        },
        navigator: {
            enabled: false
        },
        scrollbar: {
            enabled: false
        },
        tooltip: {
            crosshairs: true,
            shared: true,
            formatter: function () {
                return formatTooltip(this);
            },
            useHTML: true
        },
        xAxis: {
            type: "datetime",
            min: Date.parse(graphStartDate),
            max: Date.parse(dateRangeEnd),
            title: {
                text: "Period",
                style: boldStyle
            },
            tickPixelInterval: 125,
            labels: {
                style: normalStyle
            },
            dateTimeLabelFormats: {
                day: "%d/%m/%y",
                week: "%d/%m/%y",
                month: "%d/%m/%y",
                year: "%Y"
            },
            plotLines: isAccountStartVisible
                ? [
                      {
                          color: "#D3D3D3",
                          label: {
                              text: "Start",
                              align: "left",
                              x: 10
                          },
                          marker: {
                              enabled: false
                          },
                          value: series[0].data[0].x,
                          width: 1
                      }
                  ]
                : [],
            minRange: hoursPerDay * millisPerHour,
            minPadding: 0,
            maxPadding: 0,
            ordinal: false
        },
        yAxis: [
            {
                min: minYValue,
                max: maxYValue,
                id: "valuation",
                title: {
                    style: boldStyle
                },
                labels: {
                    formatter: function () {
                        return formatChartMoney(this.value, 0);
                    },
                    style: normalStyle
                },
                minPadding: 0,
                maxPadding: 0,
                tickPixelInterval: 25,
                opposite: false
            }
        ],
        series
    };

    if (isHome) {
        config.chart.height = 350;
        config.chart.backgroundColor = "rgba(255, 255, 255, 0)";
        config.chart.style.color = theme.tertiary;
        config.credits.style = { color: theme.tertiary };
    }

    return config;
};

const createValVsMimoSeries = (series, isHome, data, account, selectedAccounts, theme) => {
    const valuationCoords = asCoordinateArray(data.data[account] || []);
    const valuationName = isHome
        ? "Portfolio total"
        : accountWithType(account, selectedAccounts) + " valuation";
    const valuationLineWidth = 2;
    const valuationColour = isHome ? theme.tertiary : theme.primary;

    createSeries(
        series,
        valuationName,
        "valuation",
        valuationLineWidth,
        valuationColour,
        valuationCoords
    );
    series[0].marker = { enabled: false };
    series[0].events = { legendItemClick: () => false };
};

const createMimoSeries = (series, data, account, selectedAccounts) => {
    const range = asTuple(data.data[account], data.valVsMimo[account]);
    const mimoCoords = asCoordinateArray(data.valVsMimo[account]);
    const mimoName =
        accountWithType(account, selectedAccounts) +
        " payments / withdrawals " +
        grossOrNet(data.netOfFeesText);
    const mimoLineWidth = 0.6;
    const mimoColour = "#000000";
    const zIndex = 0.1;

    createSeries(series, mimoName, "valuation", mimoLineWidth, mimoColour, mimoCoords);
    createZIndexedSeries(
        series,
        "Range",
        "valuation",
        0,
        "#D3D3D3",
        range,
        "arearange",
        ":previous",
        zIndex,
        0
    );
    series[1].marker = { enabled: false };
    series[1].events = { legendItemClick: () => false };
};

const generateSeries = (isHome, data, account, selectedAccounts, theme) => {
    const series = [];

    createValVsMimoSeries(series, isHome, data, account, selectedAccounts, theme);
    if (!isHome) {
        createMimoSeries(series, data, account, selectedAccounts);
    }
    return series;
};
